import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://ymytutoring.com/api/client';

interface QuizPaymentData {
  quiz_session_id: number;
  course_name: string;
  quiz_date: string;
  price: number;
  client_name: string;
  client_phone: string;
  available_credit: number;
  credit_limit: number;
  total_debt: number;
  token: string;
}

export const QuizPaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<QuizPaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      loadPaymentInfo();
    }
  }, [token]);

  const loadPaymentInfo = async () => {
    try {
      setLoading(true);
      setError('');

      const apiBase = API_BASE.replace('/api/client', '/api');
      const url = `${apiBase}/app/quiz/payment/info/${encodeURIComponent(token!)}`;

      const response = await axios.get(url);

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'فشل في تحميل بيانات الدفع');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'فشل في تحميل بيانات الدفع';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreditPurchase = async () => {
    if (!data || processing) return;

    try {
      setProcessing(true);
      setError('');

      const apiBase = API_BASE.replace('/api/client', '/api');
      const response = await axios.post(`${apiBase}/app/quiz/payment/credit-purchase`, {
        token: data.token,
      });

      if (response.data.success) {
        // Try deep link redirect for mobile webview
        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url;
        }
        // Show success as fallback (deep link may not work in desktop browser)
        setSuccess(true);
      } else {
        setError(response.data.message || 'فشل في إتمام العملية');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'فشل في إتمام العملية';
      setError(message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayNow = () => {
    if (!token) return;
    // Redirect to the pay-now endpoint which will redirect to MyFatoorah
    const apiBase = API_BASE.replace('/api/client', '/api');
    window.location.href = `${apiBase}/app/quiz/payment/pay-now/${encodeURIComponent(token)}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-KW', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">خطأ</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">تم بنجاح!</h2>
          <p className="text-gray-600 mb-2">تم تسجيلك في الاختبار على الإعانة</p>
          <p className="text-sm text-gray-500">يمكنك العودة للتطبيق الآن</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Main payment choice page
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-blue-600 to-blue-700 text-white">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold">دفع اختبار</h1>
              <p className="text-blue-100 text-sm">اختر طريقة الدفع</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4">
        {/* Quiz Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">{data.course_name}</h2>
              <p className="text-gray-500 text-sm mt-1">{formatDate(data.quiz_date)}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">الطالب</span>
              <span className="font-medium text-gray-900">{data.client_name}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">الهاتف</span>
              <span className="font-medium text-gray-900 ltr" dir="ltr">{data.client_phone}</span>
            </div>
            <div className="border-t border-gray-200 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-medium">المبلغ المطلوب</span>
              <span className="text-xl font-bold text-blue-600">{formatCurrency(data.price)}</span>
            </div>
          </div>
        </div>

        {/* Credit Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            معلومات الحساب
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">حد الإعانة</span>
              <span className="font-medium">{formatCurrency(data.credit_limit)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">المديونية الحالية</span>
              <span className="font-medium text-red-600">{formatCurrency(data.total_debt)}</span>
            </div>
            <div className="border-t border-gray-200 my-1"></div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-medium">المتاح</span>
              <span className="font-bold text-green-600">{formatCurrency(data.available_credit)}</span>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Payment Buttons */}
        <div className="space-y-3 mb-8">
          {/* Pay Now Button */}
          <button
            onClick={handlePayNow}
            disabled={processing}
            className="w-full bg-gradient-to-l from-blue-600 to-blue-700 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            ادفع الآن
          </button>

          {/* Credit Button */}
          {data.available_credit >= data.price ? (
            <button
              onClick={handleCreditPurchase}
              disabled={processing}
              className="w-full bg-gradient-to-l from-emerald-500 to-green-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  جاري المعالجة...
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  على الإعانة
                </>
              )}
            </button>
          ) : (
            <div className="w-full bg-gray-100 text-gray-500 py-4 rounded-2xl font-bold text-lg text-center">
              <p>على الإعانة</p>
              <p className="text-sm font-normal mt-1">الرصيد المتاح غير كافٍ</p>
            </div>
          )}
        </div>

        {/* Security note */}
        <div className="text-center pb-8">
          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>عملية آمنة ومشفرة</span>
          </div>
        </div>
      </div>
    </div>
  );
};
